<template>
  <div
    class="offcanvas offcanvas-fullscreen-sm-down offcanvas-end"
    tabindex="-1"
    id="offcanvaInfo"
    ref="offcanvaInfo"
    data-bs-backdrop="static"
  >
    <div class="bg-body-secondary border offcanvas-header py-2">
      {{ $t('Customer Detail')  }}
      <button
        type="button"
        style="font-size: small;"
        class="btn-close shadow-none text-reset"
        data-bs-dismiss="offcanvas"
        aria-label="Close"
      />
    </div>

    <div
      class="offcanvas-body"
      v-if="loading"
    >
      <placeholder-content :is-load="true" />
    </div>
    <div
      class="offcanvas-body p-0"
      v-else
    >
      <div class="border-bottom py-2 px-3">
        <div class="row g-3 py-2">
          <b style="font-size: 18px;">
            {{ data.fullname }}
            <span
              v-if="data.customer_group"
              class="badge rounded-pill bg-primary ms-1"
              style="font-size: 11px;"
            >
              {{ data.customer_group }}
            </span>
          </b>
          <div
            class="d-flex mt-0 "
            style="font-size: 13px;"
          >
            <label
              class="pe-2"
            >
              {{ $t('Last Update') + ': ' + formatDateTime(data.updated_at) }}
            </label>
          </div>
        </div>
      </div>
      <div class="card m-3 mb-0">
        <div class="card-header">
          <i class="fa-solid fa-circle-info me-2" />
          {{ $t('Customer Information') }}
        </div>
        <div class="card-body pb-0">
          <div class="row" style="font-size: 13px;">
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-users me-1" />
                {{ $t('Customer Group') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              {{ data.customer_group || '-' }}
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-code-branch me-1" />
                {{ $t('Branch') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              {{ data.branch || '-' }}
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-envelope me-1" />
                {{ $t('Email') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              <a v-if="data.email"
                :href="'mailto:' + data.email">{{
                  data.email
                }}
              </a>
              <template v-else>
                -
              </template>
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-id-card me-1" />
                {{ $t('National ID') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              {{ data.national_id || '-' }}
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-brands fa-line me-1" />
                {{ $t('Line ID') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              {{ data.line_id || '-' }}
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-phone me-1" />
                {{ $t('Telephone') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              <a v-if="data.telephone1"
                :href="'tel:' + data.telephone1">{{
                  data.telephone1
                }}
              </a>
              <template v-else>
                -
              </template>
              <template v-if="data.telephone2">
                ,
                <a
                  :href="'tel:' + data.telephone2">{{
                    data.telephone2
                  }}
                </a>
              </template>
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-user-pen me-1" />
                {{ $t('Create By') + ':' }}
              </b>
            </div>
            <div class="col-6 col-md-4 mb-3">
              {{ data.created_by || '-' }}
            </div>
            <div class="col-6 col-md-2 mb-3">
              <b>
                <i class="fa-solid fa-map-location-dot me-1" />
                {{ $t('Address') + ':' }}
              </b>
            </div>
            <div
              v-if="
                (data.address || false) &&
                (data.sub_district || false) &&
                (data.district || false) &&
                (data.province || false) &&
                (data.postal_code || false)
              "
              class="col-6 col-md-4 mb-3">
              {{
                data.address + ' ' +
                data.sub_district + ' ' +
                data.district + ' ' +
                data.province + ' ' +
                data.postal_code
              }}
            </div>
            <div v-else class="col-6 col-md-4 mb-3">
              -
            </div>
          </div>
        </div>
      </div>

      <!-- Loyalty points — only shown when the staff has loyalty_point_view
           (a 403 on the points fetch keeps the whole card hidden). -->
      <div
        v-if="loyalty.visible"
        class="card m-3 mb-0"
      >
        <div class="card-header d-flex justify-content-between align-items-center">
          <span>
            <i class="fa-solid fa-star me-2" />
            {{ $t('Loyalty Points') }}
          </span>
          <button
            type="button"
            class="btn btn-sm btn-outline-primary"
            @click="openAdjust"
          >
            <i class="fa-solid fa-sliders me-1" />
            {{ $t('Adjust Points') }}
          </button>
        </div>
        <div class="card-body pb-2">
          <div
            v-if="loyalty.loading"
            class="text-center py-3"
          >
            <i class="fa-solid fa-spinner fa-spin" />
          </div>
          <template v-else>
            <div class="d-flex justify-content-between align-items-center mb-3">
              <span class="text-muted">{{ $t('Total Points') }}</span>
              <span class="fw-bold" style="font-size: 1.25rem;">
                {{ formatPoints(loyalty.total) }}
              </span>
            </div>
            <div
              v-for="p in loyalty.programs"
              :key="p.program_id"
              class="d-flex justify-content-between border-top py-1"
              style="font-size: 13px;"
            >
              <span>{{ p.program_name }}</span>
              <span class="fw-semibold">{{ formatPoints(p.balance) }}</span>
            </div>
            <div
              v-if="!loyalty.programs.length"
              class="text-muted text-center py-2"
              style="font-size: 13px;"
            >
              {{ $t('No points yet') }}
            </div>

            <!-- Inline adjust form (gated server-side by loyalty_point_adjust) -->
            <div
              v-if="adjust.open"
              class="border rounded p-2 mt-3 bg-light"
            >
              <div class="fw-semibold mb-2" style="font-size: 13px;">
                <i class="fa-solid fa-sliders me-1" />
                {{ $t('Adjust Points') }}
              </div>
              <div class="row g-2">
                <div class="col-12">
                  <label class="form-label mb-1" style="font-size: 12px;">
                    {{ $t('Program') }} <span class="text-danger">*</span>
                  </label>
                  <select
                    v-model="adjust.program_id"
                    class="form-select form-select-sm"
                  >
                    <option value="">{{ $t('Select') + '...' }}</option>
                    <option
                      v-for="opt in adjust.programOptions"
                      :key="opt.id"
                      :value="opt.id"
                    >
                      {{ opt.name }}
                    </option>
                  </select>
                </div>
                <div class="col-12">
                  <label class="form-label mb-1" style="font-size: 12px;">
                    {{ $t('Points') }} <span class="text-danger">*</span>
                    <span class="text-muted">({{ $t('use negative to deduct') }})</span>
                  </label>
                  <input
                    type="number"
                    step="1"
                    class="form-control form-control-sm"
                    v-model.number="adjust.points"
                    :placeholder="$t('Enter points') + '...'"
                  >
                </div>
                <div class="col-12">
                  <label class="form-label mb-1" style="font-size: 12px;">
                    {{ $t('Reason') }} <span class="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model.trim="adjust.reason"
                    :placeholder="$t('Enter reason') + '...'"
                  >
                </div>
                <div class="col-12 d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    class="btn btn-sm btn-light border"
                    @click="adjust.open = false"
                  >
                    {{ $t('Cancel') }}
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-primary"
                    :disabled="adjust.saving || !adjust.program_id || !adjust.points || !adjust.reason"
                    @click="submitAdjust"
                  >
                    <i class="fa-solid fa-spinner fa-spin me-1" v-if="adjust.saving" />
                    {{ $t('Save') }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Point history (ledger) -->
            <div class="border-top mt-3 pt-2">
              <button
                type="button"
                class="btn btn-sm btn-link p-0 text-decoration-none"
                @click="toggleHistory"
              >
                <i
                  class="fa-solid me-1"
                  :class="history.open ? 'fa-chevron-down' : 'fa-chevron-right'"
                />
                {{ $t('Point History') }}
              </button>
              <div v-if="history.open" class="mt-2">
                <div
                  v-if="history.loading"
                  class="text-center py-2"
                >
                  <i class="fa-solid fa-spinner fa-spin" />
                </div>
                <template v-else>
                  <div
                    v-for="row in history.items"
                    :key="row.id"
                    class="d-flex justify-content-between border-bottom py-1"
                    style="font-size: 12px;"
                  >
                    <div>
                      <span class="badge me-1" :class="historyBadgeClass(row.type)">
                        {{ $t('point_type_' + row.type) }}
                      </span>
                      <span class="text-muted">{{ row.program_name }}</span>
                      <div class="text-muted" style="font-size: 11px;">
                        {{ formatDateTime(row.created_at) }}
                        <template v-if="row.reason"> · {{ row.reason }}</template>
                        <template v-else-if="row.bill_no"> · {{ row.bill_no }}</template>
                      </div>
                    </div>
                    <span
                      class="fw-semibold"
                      :class="(+row.points) < 0 ? 'text-danger' : 'text-success'"
                    >
                      {{ (+row.points) > 0 ? '+' : '' }}{{ formatPoints(row.points) }}
                    </span>
                  </div>
                  <div
                    v-if="!history.items.length"
                    class="text-muted text-center py-2"
                    style="font-size: 12px;"
                  >
                    {{ $t('No Available Data') }}
                  </div>
                  <button
                    v-if="history.items.length < history.total"
                    type="button"
                    class="btn btn-sm btn-light border w-100 mt-2"
                    :disabled="history.loading"
                    @click="loadMoreHistory"
                  >
                    {{ $t('Load More') }}
                  </button>
                </template>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div
      class="bg-light border-top justify-content-center modal-footer py-2"
      v-if="!loading"
    >
      <div class="btn-group dropdown">
        <button type="button" class="btn btn-sm btn-primary dropdown-toggle" data-bs-toggle="dropdown" aria-expanded="false">
          <i class="fa-solid fa-ellipsis me-1" />
          {{ $t('More Action') }}
        </button>
        <ul class="dropdown-menu">
          <li>
            <a
              class="dropdown-item"
              data-bs-dismiss="offcanvas"
              aria-label="Close"
              @click="() => {
                $parent.offcanvasFroms.open = true;
                $parent.offcanvasFroms.viewOnly = false;
                $parent.offcanvasFroms.customerId = customerId;
                $parent.offcanvasFroms.type = 'EDIT';
              }"
            >
              <i class="fa-solid fa-square-pen" />
              {{ $t('Edit') }}
            </a>
          </li>
          <li>
            <a
              class="dropdown-item"
              @click="handleDelete()"
            >
              <i class="fa-solid fa-trash-can" />
              {{ $t('Delete') }}
            </a>
          </li>
        </ul>
      </div>
      <button
        type="button"
        data-bs-dismiss="offcanvas"
        class="border-dark-subtle btn-sm btn btn-light me-2 ms-2"
      >
        {{ $t('Cancel') }}
      </button>
    </div>
  </div>
</template>

<script lang="js">
import { Customers } from '@/services/endpoint/customers.service';
import { Loyalty } from '@/services/endpoint/loyalty.service';
import PlaceholderContent from '@/plugins/PlaceholderContent.vue';
import moment from "moment";

export default {
  props: {
    customerId: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      loading: true,
      data: {
        fullname: '',
        customer_group: '',
        branch: '',
        email: '',
        telephone1: '',
        telephone2: '',
        line_id: '',
        address: '',
        sub_district: '',
        district: '',
        province: '',
        postal_code: '',
        created_by: '',
        updated_at: ''
      },
      loyalty: {
        visible: false,
        loading: false,
        total: 0,
        programs: []
      },
      history: {
        open: false,
        loading: false,
        loaded: false,
        items: [],
        total: 0,
        page: 1,
        limit: 10
      },
      adjust: {
        open: false,
        saving: false,
        program_id: '',
        points: '',
        reason: '',
        programOptions: []
      }
    }
  },
  components: {
    PlaceholderContent
  },
  methods: {
    async getInfoData() {
      await Customers.getInfo(this.customerId).then(res => {
        this.data = res.data.data;
      }).catch(err => {
        throw err;
      });
    },
    formatDateTime(value) {
      return moment(value).format('YYYY-MM-DD HH:mm:ss');
    },
    formatPoints(value) {
      const n = parseInt(value) || 0;
      return n.toLocaleString();
    },
    // Server is the source of truth — FE only renders. Normalize the two shapes
    // the points endpoint may return: { data: { total_balance, programs[] } }
    // or a flat { data: [ {program_id, program_name, balance} ] }.
    async getPointsData() {
      this.loyalty.loading = true;
      try {
        const res = await Loyalty.customerPoints(this.customerId);
        const d = res.data?.data ?? res.data ?? {};
        let programs = [];
        let total = 0;
        if (Array.isArray(d)) {
          programs = d;
          total = d.reduce((s, p) => s + (parseInt(p.balance) || 0), 0);
        } else {
          programs = d.programs || [];
          total = (d.total_balance ?? d.total) != null
            ? (parseInt(d.total_balance ?? d.total) || 0)
            : programs.reduce((s, p) => s + (parseInt(p.balance) || 0), 0);
        }
        this.loyalty.programs = programs;
        this.loyalty.total = total;
        this.loyalty.visible = true;
      } catch (err) {
        // 403 (no loyalty_point_view) or 404 (loyalty off) → keep the card hidden.
        this.loyalty.visible = false;
      } finally {
        this.loyalty.loading = false;
      }
    },
    toggleHistory() {
      this.history.open = !this.history.open;
      if (this.history.open && !this.history.loaded) {
        this.history.page = 1;
        this.history.items = [];
        this.fetchHistory();
      }
    },
    async fetchHistory() {
      this.history.loading = true;
      try {
        const queryString = '?' + $.param({ page: this.history.page, limit: this.history.limit });
        const res = await Loyalty.customerPointsHistory(this.customerId, queryString);
        const rows = res.data?.data ?? [];
        this.history.items = this.history.page === 1 ? rows : this.history.items.concat(rows);
        this.history.total = res.data?.meta?.total ?? this.history.items.length;
        this.history.loaded = true;
      } catch (err) {
        this.$toast.warn(this.$t(err?.response?.data?.message));
      } finally {
        this.history.loading = false;
      }
    },
    loadMoreHistory() {
      this.history.page += 1;
      this.fetchHistory();
    },
    historyBadgeClass(type) {
      const map = {
        earn: 'bg-success',
        redeem: 'bg-primary',
        adjust: 'bg-info',
        expire: 'bg-secondary',
        reverse: 'bg-warning text-dark'
      };
      return map[type] || 'bg-secondary';
    },
    async openAdjust() {
      this.adjust.open = true;
      this.adjust.program_id = '';
      this.adjust.points = '';
      this.adjust.reason = '';
      // Load the full program list for the dropdown (adjust may target a program
      // the customer has no balance in yet).
      if (!this.adjust.programOptions.length) {
        try {
          const res = await Loyalty.listPrograms('?' + $.param({ limit: 100 }));
          const rows = res.data?.data ?? [];
          this.adjust.programOptions = rows.map(p => ({ id: p.id, name: p.name || p.code }));
        } catch (err) {
          // Fall back to programs the customer already holds points in.
          this.adjust.programOptions = this.loyalty.programs.map(p => ({
            id: p.program_id,
            name: p.program_name
          }));
        }
      }
    },
    async submitAdjust() {
      this.adjust.saving = true;
      try {
        await Loyalty.adjustCustomerPoints(this.customerId, {
          program_id: this.adjust.program_id,
          points: parseInt(this.adjust.points) || 0,
          reason: this.adjust.reason
        });
        this.$toast.success("<B>" + this.$t('Success') + "</B>\n" + this.$t('Adjust Points Success!'));
        this.adjust.open = false;
        await this.getPointsData();
        // Refresh history if it was already shown.
        if (this.history.open) {
          this.history.page = 1;
          this.history.loaded = false;
          this.fetchHistory();
        }
      } catch (err) {
        if (err?.response?.status === 403) {
          this.$toast.warn(this.$t('You do not have permission to access this page.'));
        } else {
          this.$toast.warn(this.$t(err?.response?.data?.message) || this.$t('Failed to adjust points'));
        }
      } finally {
        this.adjust.saving = false;
      }
    },
    async handleDelete() {
      try {
        let self = this;
        let title = this.$t("Delete Customer") + '?';
        let html = this.$t("You will not be able to undo this action.");
        let confirmButtonText =
          '<i class="fas fa-check-circle me-1"></i>' + this.$t("Confirm");
        this.$swal({
          icon: "warning",
          iconColor: "#FF7043",
          title: title,
          html: html,
          showCancelButton: true,
          cancelButtonText: this.$t("Cancel"),
          confirmButtonText: confirmButtonText,
          buttonsStyling: false,
          customClass: {
            confirmButton: "btn btn-primary me-2",
            cancelButton: "btn btn-danger",
          },
          focusConfirm: false,
          showLoaderOnConfirm: true,
          preConfirm: async function () {
            return Customers.delete(self.customerId)
              .then((response) => {
                self.$parent.fetchData();
                self.$toast.success("<B>" + self.$t('Success') + "</B>\n" + self.$t('Delete Customer Success!'));
                $(self.$refs.offcanvaInfo).offcanvas('hide');
              })
              .catch((error) => {
                if (error?.response?.status === 403) {
                  self.$swal.update({
                    title: self.$t('Access Denied'),
                    confirmButtonText: '<i class="fas fa-redo me-1"></i>' + self.$t("Try again"),
                    text: self.$t('You do not have permission to access this page.')
                  });
                  self.$swal.showValidationMessage(
                    self.$t(error?.response?.data?.message)
                  )
                } else {
                  self.$toast.warn(error?.response?.data?.message);
                }
              })
          },
        });
      } catch (error) {
      }
    },
  },
  async mounted() {
    $(this.$refs.offcanvaInfo).offcanvas('show');

    $(this.$refs.offcanvaInfo).on('shown.bs.offcanvas',async () => {
      this.loading = true;
      try {
        await this.getInfoData();
        this.loading = false;
        // Points load independently — a 403/404 here must not block the detail view.
        this.getPointsData();
      } catch (err) {
      }
    });

    $(this.$refs.offcanvaInfo).on('hidden.bs.offcanvas', () => {
      this.$parent.formInfo.open = false;
    });
  }
}
</script>
